import FormattedMessage from 'I18n/components/FormattedMessage';
import { useCallback, useEffect, useMemo } from 'react';
import { DEAL_TYPE_ID, TICKET_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { isReadOnly } from 'customer-data-objects/property/PropertyIdentifier';
import { NOT_SPECIFIED } from 'customer-data-objects/property/AccessLevel';
import { useSetGetDragPermissionsMessageForCard } from '../../render/options/useSetGetDragPermissionsMessageForCard';
import { useGetPropertyDefinition } from 'crm-object-board-core/properties';
import { useObjectTypeDefinition, useObjectTypeId } from 'crm-object-board-core/objectTypeDefinition';
import { jsx as _jsx } from "react/jsx-runtime";
const getEditStageDisallowedMessage = objectTypeId => {
  switch (objectTypeId) {
    case DEAL_TYPE_ID:
      return 'crmObjectBoard.cards.disallowDrag.fieldLevelPermissions.deal';
    case TICKET_TYPE_ID:
      return 'crmObjectBoard.cards.disallowDrag.fieldLevelPermissions.ticket';
    default:
      return 'crmObjectBoard.cards.disallowDrag.fieldLevelPermissions.generic';
  }
};
const getEditCardDisallowedMessage = objectTypeId => {
  switch (objectTypeId) {
    case DEAL_TYPE_ID:
      return 'crmObjectBoard.cards.disallowDrag.recordPermissions.deal';
    case TICKET_TYPE_ID:
      return 'crmObjectBoard.cards.disallowDrag.recordPermissions.ticket';
    default:
      return 'crmObjectBoard.cards.disallowDrag.recordPermissions.generic';
  }
};
export const useIsCardDraggable = () => {
  const objectTypeId = useObjectTypeId();
  const typedef = useObjectTypeDefinition();
  const getPropertyDef = useGetPropertyDefinition();
  const pipelineStagePropertyDef = useMemo(() => {
    if (!typedef) {
      return null;
    }
    const pipelineStagePropertyName = typedef.pipelineStagePropertyName;
    if (!pipelineStagePropertyName) {
      return null;
    }
    return getPropertyDef(pipelineStagePropertyName);
  }, [typedef, getPropertyDef]);
  const canEditStageProperty = useMemo(() => {
    if (!pipelineStagePropertyDef) {
      return true;
    }
    const {
      permission
    } = pipelineStagePropertyDef;
    return permission && permission.accessLevel ? permission.accessLevel === NOT_SPECIFIED : true;
  }, [pipelineStagePropertyDef]);
  const isStagePropertyReadonly = useMemo(() => {
    if (!pipelineStagePropertyDef) {
      return false;
    }
    return isReadOnly(pipelineStagePropertyDef.property);
  }, [pipelineStagePropertyDef]);
  const getIsCardDraggable = useCallback(cardData => {
    if (isStagePropertyReadonly) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "crmObjectBoard.cards.disallowDrag.readOnly.generic"
      });
    }
    if (!canEditStageProperty) {
      const message = getEditStageDisallowedMessage(objectTypeId);
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: message
      });
    }
    const cardDataWithPermissions = cardData.data;
    if (cardDataWithPermissions && cardDataWithPermissions.object && cardDataWithPermissions.object.userPermissions && !cardDataWithPermissions.object.userPermissions.currentUserCanEdit) {
      const message = getEditCardDisallowedMessage(objectTypeId);
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: message
      });
    }
    return undefined;
  }, [canEditStageProperty, isStagePropertyReadonly, objectTypeId]);
  const setter = useSetGetDragPermissionsMessageForCard();
  useEffect(() => {
    setter(getIsCardDraggable);
  }, [setter, getIsCardDraggable]);
};